/*
 * countdown-dynamic.js
 * Drives evergreen / relative countdown timers that are NOT tied to a fixed date.
 * The end time is computed in the browser (the page HTML is cached, so the server
 * cannot know "now"). Each visitor gets their own countdown, persisted in
 * localStorage so it keeps running across reloads, and it restarts when it hits
 * zero so it never expires. The first view starts a few minutes "into" the
 * duration so it never looks like it was just freshly started.
 *
 * Markup contract (rendered by snippets/countdown-timer.liquid):
 *   <div class="countdown-timer ..." data-dynamic-countdown
 *        data-duration="43200"            // total seconds
 *        data-storage-key="dyncd-<block>-43200"
 *        data-timer-id="<block id>"
 *        [data-leading-zero]>
 *     ... [data-days] [data-hours] [data-minutes] [data-seconds] ...
 *   </div>
 */
(function () {
  "use strict";

  var DAY = 86400000;
  var HOUR = 3600000;
  var MIN = 60000;
  var SEC = 1000;

  // Keep one interval per timer id so re-inits (theme editor) don't stack.
  var REGISTRY = {};

  function pad(n, lead) {
    n = n < 0 ? 0 : n | 0;
    return lead && n < 10 ? "0" + n : "" + n;
  }

  function initTimer(el) {
    var durationSec = parseInt(el.getAttribute("data-duration"), 10);
    if (!durationSec || durationSec < 1) return;
    var durationMs = durationSec * 1000;

    var id = el.getAttribute("data-timer-id") || el.getAttribute("data-storage-key") || "";
    var key = el.getAttribute("data-storage-key") || "dyncd-" + durationSec;
    var lead = el.hasAttribute("data-leading-zero");

    var dEl = el.querySelector("[data-days]");
    var hEl = el.querySelector("[data-hours]");
    var mEl = el.querySelector("[data-minutes]");
    var sEl = el.querySelector("[data-seconds]");
    if (!dEl || !hEl || !mEl || !sEl) return;

    if (id && REGISTRY[id]) clearInterval(REGISTRY[id]);

    // Shave a small random slice off so the remaining time looks organic
    // (an "odd" number) instead of the round full duration on first view.
    function jitter() {
      var max = Math.min(19 * MIN, Math.floor(durationMs * 0.15));
      return Math.floor(Math.random() * (max + 1));
    }
    function freshEnd() {
      return Date.now() + durationMs - jitter();
    }
    function store(v) {
      try { localStorage.setItem(key, String(v)); } catch (e) {}
    }
    function load() {
      try { return parseInt(localStorage.getItem(key), 10); } catch (e) { return NaN; }
    }

    var end = load();
    if (!end || isNaN(end) || end <= Date.now()) {
      end = freshEnd();
      store(end);
    }

    function render(rem) {
      var d = Math.floor(rem / DAY); rem -= d * DAY;
      var h = Math.floor(rem / HOUR); rem -= h * HOUR;
      var m = Math.floor(rem / MIN); rem -= m * MIN;
      var s = Math.floor(rem / SEC);
      dEl.textContent = pad(d, lead);
      hEl.textContent = pad(h, lead);
      mEl.textContent = pad(m, lead);
      sEl.textContent = pad(s, lead);
    }

    function tick() {
      var rem = end - Date.now();
      if (rem <= 0) {
        // Evergreen: never expires — start a fresh (re-jittered) cycle.
        end = freshEnd();
        store(end);
        rem = end - Date.now();
      }
      render(rem);
    }

    tick();
    var iv = setInterval(tick, 1000);
    if (id) REGISTRY[id] = iv;
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll("[data-dynamic-countdown]");
    for (var i = 0; i < nodes.length; i++) initTimer(nodes[i]);
  }

  if (document.readyState !== "loading") initAll();
  else document.addEventListener("DOMContentLoaded", function () { initAll(); });

  // Theme editor: re-init when a section is (re)loaded. Bind once.
  if (!window.__dynCountdownBound) {
    window.__dynCountdownBound = true;
    document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });
  }
})();
